// Phase 2A — Commerce Settings page.
//
// Five tabs, three live (Coupons / Delivery / Taxes) and two visible
// but disabled (Loyalty / Referrals "Coming Soon") so tenants see the
// roadmap without retraining nav when Phase 3 ships.
//
// Coupons + Taxes are per-CATALOG (multi-catalog tenants need
// different rules per catalog). Delivery is tenant-level.

import React, { useEffect, useState, useRef } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import MemberLoyaltySection from "../components/commerce/MemberLoyaltySection";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, Tag, Truck, Percent, Sparkles, Gift, CreditCard, Users, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "../components/ConfirmModal";
import {
  centsToDollarInput,
  formatDollarsFromCents,
  isValidDollarInput,
  parseDollarsToCents,
} from "../lib/moneyInput";

const TABS = [
  { key: "coupons",   label: "Coupons",   icon: Tag,     ready: true },
  { key: "delivery",  label: "Delivery",  icon: Truck,   ready: true },
  { key: "taxes",     label: "Taxes",     icon: Percent, ready: true },
  // Clover POS Integration — Milestone 1 (Feb 2026). Tenant POS
  // configuration lives here as a peer of the existing commerce
  // tabs. It is purely additive; existing tenants land on Coupons
  // by default and never see any change unless they open the tab.
  { key: "payments",  label: "Payments",  icon: CreditCard, ready: true },
  // Level 1 — Tenant Membership Foundation (Aug 2026). Purely additive,
  // same pattern as Payments above: existing tenants land on Coupons
  // by default and see no change unless they open this tab. Kept
  // deliberately separate from Loyalty (points/rewards, still "Coming
  // Soon") — membership identity and points are different features on
  // different timelines.
  { key: "membership", label: "Membership", icon: Users, ready: true },
  // Loyalty Points Phase 1 (Sep 2026). Purely additive, same pattern as
  // Membership above: existing tenants land on Coupons by default and see
  // no change unless they open this tab, and the program itself is OFF
  // for every tenant until the account owner turns it on.
  { key: "loyalty",   label: "Loyalty",   icon: Sparkles, ready: true },
  { key: "referrals", label: "Referrals", icon: Gift,    ready: false },
];

export default function CommerceSettingsPage() {
  const [tab, setTab] = useState("coupons");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commerce/settings");
      setSettings(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load settings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchSettings(); }, []);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold" data-testid="commerce-settings-title">Commerce Settings</h1>
          <p className="text-sm text-muted-foreground">Coupon programs, delivery rules, tax configuration and loyalty points. Referrals are coming in a future release.</p>
        </div>

        <div className="flex flex-wrap gap-2 border-b" data-testid="commerce-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => t.ready && setTab(t.key)}
                disabled={!t.ready}
                aria-disabled={!t.ready}
                data-testid={`commerce-tab-${t.key}`}
                className={
                  "px-3 py-2 text-sm rounded-t-md border-b-2 transition flex items-center gap-2 " +
                  (active ? "border-emerald-600 text-emerald-700 font-medium" :
                            "border-transparent text-muted-foreground hover:text-foreground") +
                  (t.ready ? "" : " opacity-50 cursor-not-allowed pointer-events-none")
                }
              >
                <Icon className="w-4 h-4"/>
                {t.label}
                {!t.ready && <Badge variant="outline" className="text-[10px]">Soon</Badge>}
              </button>
            );
          })}
        </div>

        {loading && <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading…</div>}
        {!loading && settings && tab === "coupons"  && <CouponsPane settings={settings} onChange={fetchSettings} />}
        {!loading && settings && tab === "delivery" && <DeliveryPane settings={settings} onChange={fetchSettings} />}
        {!loading && settings && tab === "taxes"    && <TaxesPane settings={settings} onChange={fetchSettings} />}
        {!loading && tab === "payments"  && <PaymentsPane />}
        {!loading && settings && tab === "membership" && <MembershipPane settings={settings} onChange={fetchSettings} />}
        {!loading && tab === "loyalty"   && <LoyaltyPane />}
        {!loading && tab === "referrals" && <ComingSoonPane title="Referral Program" blurb="Give customers a shareable code. Reward referrer + new customer automatically."/>}
      </div>
    </DashboardLayout>
  );
}

// ===================================================================
// Coupons pane — per-catalog post-purchase program.
// ===================================================================
function CouponsPane({ settings, onChange }) {
  const catalogs = settings.catalogs || [];
  if (catalogs.length === 0) {
    return <Card className="p-6 text-sm text-muted-foreground">No catalogs yet. Create a catalog first to configure a coupon program.</Card>;
  }
  return (
    <div className="space-y-4">
      {catalogs.map(c => <CouponCatalogCard key={c.id} catalog={c} onChange={onChange} />)}
    </div>
  );
}

function CouponCatalogCard({ catalog, onChange }) {
  const [cfg, setCfg] = useState(catalog.coupon_config || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/commerce/settings/catalogs/${catalog.id}/coupons`, cfg);
      toast.success(`${catalog.name} — coupon settings saved`);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid={`coupon-catalog-${catalog.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold">{catalog.name}</div>
          <div className="text-xs text-muted-foreground">{catalog.currency} · catalog id <span className="font-mono">{catalog.id.slice(0, 8)}…</span></div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} onChange={e => setCfg({...cfg, enabled: e.target.checked})} data-testid={`coupon-enabled-${catalog.id}`}/>
          {cfg.enabled ? <Badge className="bg-emerald-600">ON</Badge> : <Badge variant="outline">OFF</Badge>}
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Field label="Discount %">
          <select value={cfg.discount_percent || 10} onChange={e => setCfg({...cfg, discount_percent: parseInt(e.target.value)})} data-testid={`coupon-discount-${catalog.id}`} className="w-full border rounded px-2 py-1.5">
            <option value={5}>5%</option>
            <option value={10}>10%</option>
            <option value={15}>15%</option>
          </select>
        </Field>
        <Field label="Activation delay (days)">
          <Input type="number" min="0" value={cfg.activation_delay_days ?? 7} onChange={e => setCfg({...cfg, activation_delay_days: parseInt(e.target.value || 0)})} data-testid={`coupon-activation-${catalog.id}`}/>
        </Field>
        <Field label="Expiration (days)">
          <Input type="number" min="1" value={cfg.expiration_days ?? 7} onChange={e => setCfg({...cfg, expiration_days: parseInt(e.target.value || 1)})} data-testid={`coupon-expiration-${catalog.id}`}/>
        </Field>
        <Field label="Min. order amount">
          <Input type="number" min="0" step="0.01" value={cfg.minimum_order_amount ?? 0} onChange={e => setCfg({...cfg, minimum_order_amount: parseFloat(e.target.value || 0)})} data-testid={`coupon-min-${catalog.id}`}/>
        </Field>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid={`coupon-save-${catalog.id}`}>{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save"}</Button>
      </div>
    </Card>
  );
}

// ===================================================================
// Delivery pane — tenant-wide rules.
// ===================================================================
function DeliveryPane({ settings, onChange }) {
  const [d, setD] = useState(settings.delivery || {});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/commerce/settings/delivery", {
        delivery_enabled: !!d.delivery_enabled,
        free_delivery_threshold: parseFloat(d.free_delivery_threshold || 0),
        delivery_charge: parseFloat(d.delivery_charge || 0),
        maximum_service_radius_miles: parseFloat(d.maximum_service_radius_miles || 0),
      });
      toast.success("Delivery rules saved");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5" data-testid="delivery-pane">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <div className="font-semibold">Delivery rules</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Toggle Delivery on for this tenant. Rules below only apply
            when Delivery is on — otherwise all orders route through
            Pickup / Take-out only.
          </div>
        </div>
        <label
          className="flex flex-col items-end gap-1 text-sm cursor-pointer shrink-0"
          data-testid="delivery-enabled-label"
        >
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Delivery
            </span>
            <input
              type="checkbox"
              checked={!!d.delivery_enabled}
              onChange={e => setD({...d, delivery_enabled: e.target.checked})}
              data-testid="delivery-enabled"
              className="h-4 w-4"
            />
          </div>
          {d.delivery_enabled
            ? <Badge className="bg-emerald-600" data-testid="delivery-status-on">Delivery ON</Badge>
            : <Badge variant="outline" data-testid="delivery-status-off">Pickup only</Badge>}
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Free delivery threshold ($)">
          <Input type="number" min="0" step="0.01" value={d.free_delivery_threshold ?? 0} onChange={e => setD({...d, free_delivery_threshold: e.target.value})} data-testid="delivery-threshold"/>
        </Field>
        <Field label="Delivery charge ($)">
          <Input type="number" min="0" step="0.01" value={d.delivery_charge ?? 0} onChange={e => setD({...d, delivery_charge: e.target.value})} data-testid="delivery-charge"/>
        </Field>
        <Field label="Max service radius (miles) · placeholder">
          <Input type="number" min="0" value={d.maximum_service_radius_miles ?? 0} onChange={e => setD({...d, maximum_service_radius_miles: e.target.value})} data-testid="delivery-radius"/>
          <div className="text-[11px] text-muted-foreground mt-1">0 = unlimited. Enforced in a future release.</div>
        </Field>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="delivery-save">{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save"}</Button>
      </div>
    </Card>
  );
}

// ===================================================================
// Membership pane — Level 1 Tenant Membership Foundation (Aug 2026).
//
// Tenant-level enable/disable (mirrors DeliveryPane's toggle pattern
// above) plus a bounded, paginated read-only member list. No points,
// balances, tiers, or reward claims are ever rendered here — Level 1
// is identity + consent only.
// ===================================================================
function MembershipPane({ settings, onChange }) {
  const [enabled, setEnabled] = useState(!!settings.membership?.enabled);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  // Live-acceptance correction (Aug 2026), Correction 5 — an
  // active/inactive filter for the tenant list. Kept to just the two
  // statuses the backend actually supports (`status ∈ {active,
  // inactive}` per `services/membership.py::STATUS_VALUES`) — no
  // broader search/filter UI, which is explicitly out of scope here.
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const LIMIT = 25;

  const fetchMembers = async (nextOffset = offset, nextStatus = statusFilter) => {
    setMembersLoading(true);
    try {
      const params = { limit: LIMIT, offset: nextOffset };
      if (nextStatus) params.status = nextStatus;
      const { data } = await api.get("/commerce/members", { params });
      setMembers(data);
      setOffset(nextOffset);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load members"); }
    finally { setMembersLoading(false); }
  };
  useEffect(() => { fetchMembers(0, statusFilter); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/commerce/settings/membership", { enabled });
      setEnabled(!!data.enabled);
      toast.success(data.enabled ? "Membership enabled" : "Membership disabled");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const total = members?.total ?? 0;
  const items = members?.items ?? [];

  return (
    <div className="space-y-4">
      <Card className="p-5" data-testid="membership-pane">
        <div className="flex items-start justify-between mb-1 gap-4">
          <div>
            <div className="font-semibold">Membership program</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Let customers voluntarily join this store's membership program
              after phone verification. Their existing customer number
              becomes their Member ID. Disabling hides the enrollment
              option storefront-wide but never deletes existing member
              records.
            </div>
          </div>
          <label
            className="flex flex-col items-end gap-1 text-sm cursor-pointer shrink-0"
            data-testid="membership-enabled-label"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Membership</span>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                data-testid="membership-enabled"
                className="h-4 w-4"
              />
            </div>
            {enabled
              ? <Badge className="bg-emerald-600" data-testid="membership-status-on">Membership ON</Badge>
              : <Badge variant="outline" data-testid="membership-status-off">Membership OFF</Badge>}
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="membership-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </Card>

      <Card className="p-5" data-testid="membership-list-pane">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="font-semibold">Enrolled members</div>
          <div className="flex items-center gap-3">
            {/* Live-acceptance correction (Aug 2026), Correction 5 —
                real-time cross-browser updates are not required; the
                tenant instead gets an explicit, always-available
                Refresh action plus an automatic refresh right after
                any lifecycle change made from this same tab (see
                MemberDetailsDrawer's onChanged below). */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="membership-status-filter"
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="text-xs text-muted-foreground" data-testid="membership-total">
              {total} member{total === 1 ? "" : "s"}
            </div>
            <Button
              variant="outline" size="sm" data-testid="membership-list-refresh"
              disabled={membersLoading}
              onClick={() => fetchMembers(offset, statusFilter)}
            >
              <RefreshCw className={"w-3.5 h-3.5" + (membersLoading ? " animate-spin" : "")} />
            </Button>
          </div>
        </div>
        {membersLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…
          </div>
        )}
        {!membersLoading && items.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="membership-list-empty">
            No members enrolled yet.
          </div>
        )}
        {!membersLoading && items.length > 0 && (
          <>
            <Table data-testid="membership-list-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Member ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow
                    key={m.member_number}
                    data-testid={`membership-row-${m.member_number}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedCustomerId(m.customer_id)}
                  >
                    <TableCell className="font-mono text-xs">{m.member_number}</TableCell>
                    <TableCell>{m.customer_name || "—"}</TableCell>
                    <TableCell>{m.phone || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={m.status === "active" ? "default" : "outline"}>{m.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`membership-row-view-${m.member_number}`}
                        onClick={(e) => { e.stopPropagation(); setSelectedCustomerId(m.customer_id); }}
                      >
                        View details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-3 flex items-center justify-between">
              <Button
                variant="outline" size="sm" data-testid="membership-list-prev"
                disabled={offset === 0 || membersLoading}
                onClick={() => fetchMembers(Math.max(0, offset - LIMIT))}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                {Math.min(offset + 1, total)}–{Math.min(offset + items.length, total)} of {total}
              </span>
              <Button
                variant="outline" size="sm" data-testid="membership-list-next"
                disabled={offset + items.length >= total || membersLoading}
                onClick={() => fetchMembers(offset + LIMIT)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </Card>

      {selectedCustomerId && (
        <MemberDetailsDrawer
          customerId={selectedCustomerId}
          onClose={() => setSelectedCustomerId(null)}
          onChanged={() => fetchMembers(offset, statusFilter)}
        />
      )}
    </div>
  );
}

// ===================================================================
// Member details drawer — Correction 4/5 (Aug 2026 live-acceptance
// follow-up). Mirrors the existing retail admin drawer pattern
// (`OrderDetailsDrawer.jsx`): fixed full-height right-side panel,
// backdrop click closes, sticky header. Resolves customer/address
// fields from `GET /commerce/members/{customer_id}` — the authoritative
// tenant-scoped join — never duplicates them into local state beyond
// what's needed to render and edit.
// ===================================================================
function MemberDetailsDrawer({ customerId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "deactivate" | "reactivate" | null
  const [actionBusy, setActionBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/commerce/members/${customerId}`);
      setData(d);
      setEditName(d.customer?.name || "");
      setEditEmail(d.customer?.email || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load member");
      onClose?.();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Correction 5 — deliberately never sends `phone`. The verified
      // mobile number is the membership identity; this drawer offers
      // no phone field at all, so an ordinary, unverified edit here
      // can never change it.
      await api.patch(`/customers/${customerId}`, { name: editName, email: editEmail });
      toast.success("Member details updated");
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const runLifecycleAction = async () => {
    if (!confirmAction) return;
    setActionBusy(true);
    try {
      await api.post(`/commerce/members/${customerId}/${confirmAction}`);
      toast.success(confirmAction === "deactivate" ? "Membership deactivated" : "Membership reactivated");
      setConfirmAction(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setActionBusy(false); }
  };

  const membership = data?.membership;
  const customer = data?.customer;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="member-details-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md h-full bg-white shadow-2xl border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Member details</div>
            <h2 className="text-lg font-semibold tracking-tight font-mono" data-testid="member-details-id">
              {membership?.member_number || "…"}
            </h2>
          </div>
          <button type="button" onClick={onClose} data-testid="member-details-close" className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…
          </div>
        )}

        {!loading && membership && customer && (
          <div className="p-5 space-y-5">
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Membership</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Status</span>
                  <Badge variant={membership.status === "active" ? "default" : "outline"} data-testid="member-details-status">{membership.status}</Badge>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Joined</span>
                  <span data-testid="member-details-joined">{membership.joined_at ? new Date(membership.joined_at).toLocaleString() : "—"}</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Last updated</span>
                  <span>{membership.updated_at ? new Date(membership.updated_at).toLocaleString() : "—"}</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Enrollment source</span>
                  <span>{membership.enrollment_source || "—"}</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Consent given</span>
                  <span>{membership.consent_captured_at ? new Date(membership.consent_captured_at).toLocaleString() : "—"}</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Consent version</span>
                  <span>{membership.consent_version || "—"}</span>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Customer</div>
                {!editing && (
                  <Button variant="outline" size="sm" data-testid="member-details-edit" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                )}
              </div>
              {!editing ? (
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Name</span><span data-testid="member-details-name">{customer.name || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Phone (verified)</span><span data-testid="member-details-phone">{customer.phone || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Email</span><span data-testid="member-details-email">{customer.email || "—"}</span></div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" data-testid="member-details-edit-name" />
                  <Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="Email" data-testid="member-details-edit-email" />
                  {/* Correction 5 — no phone input here, intentionally. */}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditName(customer.name || ""); setEditEmail(customer.email || ""); }}>Cancel</Button>
                    <Button size="sm" disabled={saving} data-testid="member-details-edit-save" onClick={saveEdit}>
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                    </Button>
                  </div>
                </div>
              )}

              <div className="mt-3">
                <div className="text-xs text-slate-500 mb-1">Saved addresses</div>
                {customer.addresses && customer.addresses.length > 0 ? (
                  <ul className="space-y-1 text-xs" data-testid="member-details-addresses">
                    {customer.addresses.map((a, i) => (
                      <li key={a.address_id || i} className="text-slate-700">
                        {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.postal_code}
                        {a.is_default && <span className="ml-1 text-emerald-600">(default)</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-400" data-testid="member-details-no-address">No saved address.</div>
                )}
              </div>
            </section>

            <section className="pt-3 border-t border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Membership status</div>
              {membership.status === "active" ? (
                <Button
                  variant="outline" size="sm" data-testid="member-details-deactivate"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setConfirmAction("deactivate")}
                >
                  Deactivate Membership
                </Button>
              ) : (
                <Button
                  variant="outline" size="sm" data-testid="member-details-reactivate"
                  onClick={() => setConfirmAction("reactivate")}
                >
                  Reactivate Membership
                </Button>
              )}
            </section>

            {/* Loyalty Phase 1 (Sep 2026) — the tenant-admin points view
                and the owner-only manual adjustment control, rendered
                inside the member drawer rather than as a second place to
                look for the same member. Owner gating and the negative-
                balance confirmation are enforced server-side; the
                component simply does not render the controls for a
                non-owner. */}
            <MemberLoyaltySection customerId={customerId} />
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction === "deactivate" ? "Deactivate this membership?" : "Reactivate this membership?"}
        message={
          confirmAction === "deactivate"
            ? "The member record, Member ID, and consent history are preserved — this only removes active-member status. You can reactivate at any time."
            : "This restores active-member status under the same Member ID and original join date."
        }
        confirmLabel={confirmAction === "deactivate" ? "Deactivate" : "Reactivate"}
        tone={confirmAction === "deactivate" ? "danger" : "info"}
        busy={actionBusy}
        onCancel={() => setConfirmAction(null)}
        onConfirm={runLifecycleAction}
        testIdPrefix="member-lifecycle-confirm"
      />
    </div>
  );
}

// ===================================================================
// Taxes pane — per-catalog default rate.
// ===================================================================
function TaxesPane({ settings, onChange }) {
  const catalogs = settings.catalogs || [];
  if (catalogs.length === 0) {
    return <Card className="p-6 text-sm text-muted-foreground">No catalogs yet.</Card>;
  }
  return (
    <div className="space-y-4">
      {catalogs.map(c => <TaxCatalogCard key={c.id} catalog={c} onChange={onChange} />)}
      <Card className="p-4 text-xs text-muted-foreground" data-testid="taxes-per-product-guidance">
        The rate above is each catalog&apos;s default. To make one product tax exempt or give it its own rate, go to{" "}
        <span className="font-medium text-foreground">Retail Catalog → open the catalog → Products → Edit</span>{" "}
        on that product, and choose under <span className="font-medium text-foreground">Tax treatment</span>:
        Use catalog default, Tax exempt, or Custom tax rate (%). Changes apply to new orders only; orders already placed keep the tax they were charged.
      </Card>
    </div>
  );
}

function TaxCatalogCard({ catalog, onChange }) {
  // Display as percentage (8.25) while storing the decimal (0.0825).
  const [pct, setPct] = useState(((catalog.default_tax_rate || 0) * 100).toFixed(3).replace(/\.?0+$/, ""));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const decimal = Math.max(0, Math.min(1, parseFloat(pct || 0) / 100));
    setSaving(true);
    try {
      await api.patch(`/commerce/settings/catalogs/${catalog.id}/tax`, { default_tax_rate: decimal });
      toast.success(`${catalog.name} default tax: ${(decimal * 100).toFixed(3)}%`);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5" data-testid={`tax-catalog-${catalog.id}`}>
      <div className="font-semibold">{catalog.name}</div>
      <div className="text-xs text-muted-foreground mb-3">{catalog.currency}</div>
      <Field label="Default tax rate (%)">
        <div className="flex items-center gap-2">
          <Input type="number" min="0" max="100" step="0.001" value={pct} onChange={e => setPct(e.target.value)} className="max-w-[160px]" data-testid={`tax-rate-${catalog.id}`}/>
          <span className="text-sm text-muted-foreground">% — e.g. 8.25 for 8.25% sales tax</span>
        </div>
      </Field>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid={`tax-save-${catalog.id}`}>{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save"}</Button>
      </div>
    </Card>
  );
}

// ===================================================================
// Shared helpers
// ===================================================================
const Field = ({ label, children }) => (
  <label className="block text-xs space-y-1">
    <span className="text-muted-foreground">{label}</span>
    {children}
  </label>
);

const ComingSoonPane = ({ title, blurb }) => (
  <Card className="p-8 text-center bg-slate-50/60 border-dashed">
    <Badge variant="outline" className="mb-2">Coming Soon</Badge>
    <div className="font-display text-2xl font-semibold">{title}</div>
    <div className="text-sm text-muted-foreground max-w-md mx-auto mt-2">{blurb}</div>
  </Card>
);

// ===================================================================
// Loyalty pane — Loyalty Points Phase 1 (Sep 2026).
//
// Tenant-wide, and OFF until the account owner turns it on. Everything
// here is owner-only: points are money, and while every tenant user is
// `business_admin`, the backend gates on `companies.owner_id`.
// Non-owners see the settings read-only rather than an empty tab.
//
// The one piece of real weight is an earning-rule change: it starts a
// new earning period and clears members' partial progress toward their
// next point. The backend refuses such a change without explicit
// confirmation, so this asks for it plainly rather than burying it.
// ===================================================================
// Tenant-facing money is always dollars. Storage stays in integer cents.
const loyaltyMoney = (cents) => formatDollarsFromCents(cents) || "$0.00";

// The backend's own safe message for a rejected save, or a generic
// fallback. Handles both shapes the API uses — a structured
// `{error, message}` detail and a plain-string detail — and never
// renders an object, which React would throw on.
export function safeSaveErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string" && detail.message.trim()) {
    return detail.message;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  return "Save failed";
}

function LoyaltyPane() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState([]);
  // Business-friendly units correction (Sep 2026) — the two MONETARY
  // settings are edited as dollars and held here as the exact text the
  // tenant typed. `draft.spend_cents` / `draft.redeem_value_cents` remain
  // integer cents and remain what is sent to the API; these two strings
  // are only the boundary. Keeping the typed text (rather than
  // re-formatting the cents on every keystroke) is what lets someone type
  // "1.0" on the way to "1.05" without the field fighting them.
  const [spendText, setSpendText] = useState("");
  const [redeemValueText, setRedeemValueText] = useState("");
  // Sep 2026 — a ref, not `saving` state: two clicks landing in the same
  // tick would BOTH read the old state and both fire a PATCH. A rule
  // change starts a new earning period, so a duplicate request is not a
  // harmless retry.
  const savingRef = useRef(false);
  // Sep 2026 — optimistic concurrency. Set when a save was refused because
  // the settings changed while this page was open (409). The page must be
  // reloaded before saving again; it never retries on its own against the
  // newer version, which would silently overwrite someone else's rule.
  const [stale, setStale] = useState(false);

  const load = async () => {
    setLoading(true);
    setStale(false);
    try {
      const { data } = await api.get("/commerce/settings/loyalty");
      setCfg(data);
      setDraft(data);
      // Stored cents → two-decimal dollars, every time settings load.
      setSpendText(centsToDollarInput(data?.spend_cents));
      setRedeemValueText(centsToDollarInput(data?.redeem_value_cents));
      const h = await api.get("/commerce/settings/loyalty/history", { params: { limit: 5 } });
      setHistory(h.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to load loyalty settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !draft) {
    return (
      <Card className="p-6 text-sm text-muted-foreground" data-testid="loyalty-loading">
        <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…
      </Card>
    );
  }

  const isOwner = !!cfg?.is_owner;
  const healthy = cfg?.health?.healthy !== false;
  const earningChanged =
    parseDollarsToCents(spendText) !== Number(cfg.spend_cents) ||
    Number(draft.points_per_unit) !== Number(cfg.points_per_unit);

  const set = (k) => (e) => {
    const isCheckbox = e?.target?.type === "checkbox";
    setDraft({ ...draft, [k]: isCheckbox ? e.target.checked : Number(e.target.value) });
  };

  // A monetary field: keep the tenant's exact text, and mirror it into
  // the cents the API is given only while it is a valid amount. An
  // invalid entry leaves the last good cents value in the draft and is
  // refused at save time rather than silently rounded into something the
  // tenant did not ask for.
  const setMoney = (k, setText) => (e) => {
    const text = e.target.value;
    setText(text);
    const cents = parseDollarsToCents(text);
    if (cents != null) setDraft((d) => ({ ...d, [k]: cents }));
  };

  const MONEY_FIELDS = [
    { text: spendText, label: "Spend ($) to earn" },
    { text: redeemValueText, label: "Redemption value ($)" },
  ];

  const submit = async (confirmForfeiture) => {
    // Never persist a floating-point dollar value, and never persist a
    // figure the tenant did not actually type.
    const spendCents = parseDollarsToCents(spendText);
    const redeemValueCents = parseDollarsToCents(redeemValueText);
    const bad = MONEY_FIELDS.find((f) => !isValidDollarInput(f.text));
    if (bad || spendCents == null || redeemValueCents == null) {
      toast.error(
        `${(bad || MONEY_FIELDS[0]).label} must be an amount in dollars with at most two decimal places.`,
      );
      setConfirmOpen(false);
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { data } = await api.patch("/commerce/settings/loyalty", {
        enabled: !!draft.enabled,
        spend_cents: spendCents,
        points_per_unit: Number(draft.points_per_unit),
        redeem_points: Number(draft.redeem_points),
        redeem_value_cents: redeemValueCents,
        min_redeem_points: Number(draft.min_redeem_points),
        redeem_increment_points: Number(draft.redeem_increment_points),
        max_redeem_points_per_order: Number(draft.max_redeem_points_per_order),
        combine_with_coupons: !!draft.combine_with_coupons,
        confirm_forfeiture: !!confirmForfeiture,
        // The version this page loaded — the server applies the save only
        // if it is still the current one.
        expected_config_version: Number(cfg.config_version),
      });
      setCfg({ ...cfg, ...data });
      setDraft({ ...cfg, ...data });
      toast.success("Loyalty settings saved");
      setConfirmOpen(false);
      load();
    } catch (e) {
      // Show what the backend actually said — a rejected rule
      // relationship, or "someone else changed this while your page was
      // open" — rather than a bare "Save failed" that leaves the owner
      // with nothing to act on. The generic fallback stays for the cases
      // with no safe message (a network failure, or a 500 whose body is
      // deliberately opaque). The dialog is left OPEN and nothing is
      // marked saved, so the pane never shows an unsaved rule as applied.
      if (e?.response?.status === 409) {
        setStale(true);
        setConfirmOpen(false);
      }
      toast.error(safeSaveErrorMessage(e));
    } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {stale && (
        <Card className="p-4 border-amber-300 bg-amber-50/70" data-testid="loyalty-stale-warning">
          <div className="text-sm font-medium text-amber-900">These settings changed while you were editing</div>
          <div className="text-sm text-amber-800 mt-1">
            Your changes were not saved. Reload the latest settings, review them, and try again.
          </div>
          <Button size="sm" variant="outline" className="mt-2" onClick={load} data-testid="loyalty-reload">
            Reload settings
          </Button>
        </Card>
      )}
      {!healthy && (
        <Card className="p-4 border-amber-300 bg-amber-50/70" data-testid="loyalty-health-warning">
          <div className="text-sm font-medium text-amber-900">Loyalty is paused</div>
          <div className="text-sm text-amber-800 mt-1">
            {cfg.health?.reason || "Loyalty is temporarily unavailable."} Points already earned
            are safe, and purchases made while it is paused will be added once it resumes.
          </div>
        </Card>
      )}

      <Card className="p-5" data-testid="loyalty-pane">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-medium">Loyalty points</div>
            <div className="text-sm text-muted-foreground mt-1 max-w-xl">
              Members earn points on what they spend and can put them toward a future order.
              Points are added only after a payment is finalised, and are reversed if the order
              is refunded.
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm shrink-0">
            <input type="checkbox" checked={!!draft.enabled} onChange={set("enabled")}
                   disabled={!isOwner} data-testid="loyalty-enabled" />
            {draft.enabled ? "On" : "Off"}
          </label>
        </div>

        {!isOwner && (
          <div className="mt-3 text-sm text-muted-foreground border-l-2 pl-3"
               data-testid="loyalty-owner-only-note">
            Only the account owner can change these settings.
          </div>
        )}

        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="text-sm font-medium">Earning</div>
            <Field label="Spend ($) to earn">
              <Input type="text" inputMode="decimal" value={spendText}
                     onChange={setMoney("spend_cents", setSpendText)}
                     disabled={!isOwner} data-testid="loyalty-spend-cents" />
            </Field>
            <Field label="Points earned">
              <Input type="number" min="1" value={draft.points_per_unit}
                     onChange={set("points_per_unit")} disabled={!isOwner}
                     data-testid="loyalty-points-per-unit" />
            </Field>
            <div className="text-xs text-muted-foreground">
              Spend {loyaltyMoney(draft.spend_cents)} → earn {draft.points_per_unit}{" "}
              {Number(draft.points_per_unit) === 1 ? "point" : "points"}. Anything left over
              counts toward the next point.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Redemption</div>
            <Field label="Points required">
              <Input type="number" min="1" value={draft.redeem_points}
                     onChange={set("redeem_points")} disabled={!isOwner}
                     data-testid="loyalty-redeem-points" />
            </Field>
            <Field label="Redemption value ($)">
              <Input type="text" inputMode="decimal" value={redeemValueText}
                     onChange={setMoney("redeem_value_cents", setRedeemValueText)}
                     disabled={!isOwner} data-testid="loyalty-redeem-value" />
            </Field>
            <Field label="Minimum redemption (points)">
              <Input type="number" min="1" value={draft.min_redeem_points}
                     onChange={set("min_redeem_points")} disabled={!isOwner}
                     data-testid="loyalty-min-redeem" />
            </Field>
            <Field label="Redeem in steps of (points)">
              <Input type="number" min="1" value={draft.redeem_increment_points}
                     onChange={set("redeem_increment_points")} disabled={!isOwner}
                     data-testid="loyalty-increment" />
            </Field>
            <Field label="Most a member may use on one order (0 = no limit)">
              <Input type="number" min="0" value={draft.max_redeem_points_per_order}
                     onChange={set("max_redeem_points_per_order")} disabled={!isOwner}
                     data-testid="loyalty-max-per-order" />
            </Field>
            <label className="flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={!!draft.combine_with_coupons}
                     onChange={set("combine_with_coupons")} disabled={!isOwner}
                     data-testid="loyalty-combine-coupons" />
              Allow points and a coupon on the same order
            </label>
            <div className="text-xs text-muted-foreground">
              {draft.redeem_points}{" "}
              {Number(draft.redeem_points) === 1 ? "point" : "points"} ={" "}
              {loyaltyMoney(draft.redeem_value_cents)}. At least $1.00 of every order must
              still be paid by card or cash.
            </div>
          </div>
        </div>

        {isOwner && (
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Button onClick={() => (earningChanged ? setConfirmOpen(true) : submit(false))}
                    disabled={saving || stale} data-testid="loyalty-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
            </Button>
            {earningChanged && (
              <span className="text-xs text-amber-700" data-testid="loyalty-earning-changed-hint">
                Changing how points are earned starts a new earning period.
              </span>
            )}
          </div>
        )}
      </Card>

      {history.length > 0 && (
        <Card className="p-5" data-testid="loyalty-config-history">
          <div className="text-sm font-medium mb-3">Recent changes</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Changed by</TableHead>
                <TableHead>Earning rule</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="text-xs">
                    {h.created_at ? new Date(h.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-xs">{h.actor_email || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {h.earning_rule_changed
                      ? `${loyaltyMoney(h.previous_rule?.spend_cents)} → ${loyaltyMoney(h.new_rule?.spend_cents)} per ${h.new_rule?.points_per_unit} pt`
                      : "unchanged"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Start a new earning period?"
        message={
          "Members keep every point they have already earned. But any partial progress toward " +
          "their next point is cleared and starts again under the new rule. Refunds on older " +
          "orders are still worked out under the rule that applied when they were placed."
        }
        confirmLabel="Save the new rule"
        tone="warning"
        busy={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => submit(true)}
        testIdPrefix="loyalty-forfeiture-confirm"
      />
    </div>
  );
}

// ===================================================================
// Payments pane — Clover POS Integration Milestone 1 (Feb 2026).
//
// Tenant POS transaction mode + external-provider selection. All
// business validation happens server-side; this pane is a thin form.
// No connectivity tests, no OAuth, no secrets — those land in later
// milestones. The pane also does NOT alter existing Stripe/cash
// checkout behaviour: saving Clover configuration is inert until
// downstream routing milestones wire it up.
// ===================================================================
function PaymentsPane() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [posConfig, setPosConfig] = useState(null);
  const [effective, setEffective] = useState(null);
  const [mode, setMode] = useState("rudras_direct");
  const [provider, setProvider] = useState("clover");
  const [clover, setClover] = useState({
    environment: "sandbox",
    merchant_id: "",
    device_id: "",
    remote_app_id: "",
  });
  const [errors, setErrors] = useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/pos-config");
      setPosConfig(data.pos_config || null);
      setEffective(data.effective || null);
      const m = data.pos_config?.transaction_mode || "rudras_direct";
      setMode(m);
      if (m === "external_pos") {
        setProvider(data.pos_config?.provider || "clover");
        const c = data.pos_config?.clover || {};
        setClover({
          environment: c.environment || "sandbox",
          merchant_id: c.merchant_id || "",
          device_id: c.device_id || "",
          remote_app_id: c.remote_app_id || "",
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to load payments configuration.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setErrors({});
    let payload;
    if (mode === "rudras_direct") {
      payload = { transaction_mode: "rudras_direct" };
    } else {
      const localErr = {};
      if (!clover.merchant_id.trim()) localErr.merchant_id = "Required.";
      // device_id / remote_app_id are optional for the OAuth milestone —
      // not consumed anywhere yet; required only for a later
      // payment-terminal pairing milestone.
      if (Object.keys(localErr).length) { setErrors(localErr); return; }
      payload = {
        transaction_mode: "external_pos",
        provider,
        clover: {
          environment: clover.environment,
          merchant_id: clover.merchant_id.trim(),
          device_id: clover.device_id.trim(),
          remote_app_id: clover.remote_app_id.trim(),
        },
      };
    }
    setSaving(true);
    try {
      const { data } = await api.put("/company/pos-config", payload);
      setPosConfig(data.pos_config || null);
      setEffective(data.effective || null);
      toast.success("Configuration saved. Connection not yet tested.");
      // Milestone 2 — after saving to external_pos+clover, refresh
      // the credential status so the Clover credential card renders.
      if (mode === "external_pos") { loadCloverStatus(); }
    } catch (e) {
      const det = e.response?.data?.detail;
      const msg = (det && (det.message || det.code)) || "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ------ Milestone 2A — Clover OAuth Connect / Disconnect ------
  const [cloverStatus, setCloverStatus] = useState(null);
  const [credsBusy, setCredsBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  const loadCloverStatus = React.useCallback(async () => {
    try {
      const { data } = await api.get("/company/pos-config/clover/status");
      setCloverStatus(data);
    } catch {
      setCloverStatus(null);
    }
  }, []);
  useEffect(() => { loadCloverStatus(); }, [loadCloverStatus]);

  // Milestone 2A — handle the ?clover=connected|error return from the
  // OAuth callback. Show a toast, refresh status, then strip the query
  // params so a reload doesn't reapply the message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("clover");
    if (!status) return;
    if (status === "connected") {
      toast.success("Clover connected.");
    } else if (status === "error") {
      const code = params.get("code") || "clover_connection_failed";
      toast.error(`Clover connection failed: ${code}`);
    }
    loadCloverStatus();
    // Strip the query so the toast doesn't re-fire on refresh.
    const url = new URL(window.location.href);
    ["clover", "code", "msg", "merchant"].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, "", url.toString());
  }, [loadCloverStatus]);

  const connectClover = async () => {
    setCredsBusy(true);
    try {
      const { data } = await api.post("/company/pos-config/clover/connect", {});
      if (data?.authorize_url) {
        // Full-page navigation to Clover — merchant authorizes there,
        // then Clover redirects back to our /oauth/callback route,
        // which redirects the browser back to this settings page with
        // a ?clover= status query string.
        window.location.href = data.authorize_url;
        return;
      }
      toast.error("Clover connect returned no authorize URL.");
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || det?.code || "Connect failed"));
    } finally { setCredsBusy(false); }
  };

  const disconnectClover = async () => {
    if (!window.confirm("Disconnect Clover for this tenant? Test Connection will be unavailable until you reconnect.")) return;
    setCredsBusy(true);
    try {
      await api.post("/company/pos-config/clover/disconnect", {});
      await loadCloverStatus();
      setLastTest(null);
      toast.success("Clover disconnected.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed.");
    } finally { setCredsBusy(false); }
  };

  const runTestConnection = async () => {
    setTestBusy(true); setLastTest(null);
    try {
      const { data } = await api.post("/company/pos-config/clover/test-connection", {});
      setLastTest(data);
      if (data.ok) toast.success(`Connected to ${data.merchant_name || data.merchant_id}`);
      else toast.error(data.message || data.code || "Test failed");
    } catch (e) {
      const det = e.response?.data?.detail || "Test failed";
      toast.error(typeof det === "string" ? det : (det.message || det.code || "Test failed"));
    } finally { setTestBusy(false); }
  };

  if (loading) {
    return <div className="py-10 text-center text-sm text-muted-foreground" data-testid="payments-pane-loading">
      <Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading…
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="payments-pane">
      <Card className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-lg font-semibold">Transaction mode</div>
            <div className="text-sm text-muted-foreground">
              Choose how this tenant accepts card payments. Existing Stripe and cash workflows are unaffected on Rudras-AI direct.
            </div>
          </div>
          {effective && (
            <Badge variant="outline" data-testid="payments-effective-mode">
              Currently: {effective.transaction_mode === "external_pos"
                ? `External POS — ${effective.provider || "not set"}`
                : "Rudras-AI direct"}
            </Badge>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className={`border rounded-lg p-3 cursor-pointer ${mode === "rudras_direct" ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
            <input
              type="radio"
              name="txn-mode"
              value="rudras_direct"
              checked={mode === "rudras_direct"}
              onChange={() => setMode("rudras_direct")}
              data-testid="payments-mode-rudras-direct"
              className="mr-2"
            />
            <span className="font-medium text-sm">Rudras-AI direct</span>
            <div className="text-xs text-muted-foreground mt-1">Stripe card payments and cash workflows (current default).</div>
          </label>
          <label className={`border rounded-lg p-3 cursor-pointer ${mode === "external_pos" ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
            <input
              type="radio"
              name="txn-mode"
              value="external_pos"
              checked={mode === "external_pos"}
              onChange={() => setMode("external_pos")}
              data-testid="payments-mode-external-pos"
              className="mr-2"
            />
            <span className="font-medium text-sm">External POS</span>
            <div className="text-xs text-muted-foreground mt-1">Route card payments to an external POS terminal. Clover only in this release.</div>
          </label>
        </div>
      </Card>

      {mode === "external_pos" && (
        <Card className="p-5" data-testid="payments-external-pos-card">
          <div className="mb-3">
            <div className="font-display text-lg font-semibold">External POS provider</div>
            <div className="text-sm text-muted-foreground">Currently only Clover is supported. Additional providers will follow.</div>
          </div>

          <div className="mb-4">
            <label className="text-xs font-medium text-slate-700">Provider</label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              data-testid="payments-provider-select"
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
            >
              <option value="clover">Clover</option>
            </select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Environment">
              <select
                value={clover.environment}
                onChange={(e) => setClover({ ...clover, environment: e.target.value })}
                data-testid="payments-clover-environment"
                className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </Field>
            <Field label={<>Merchant ID {errors.merchant_id && <span className="text-rose-600 ml-1">({errors.merchant_id})</span>}</>}>
              <Input
                value={clover.merchant_id}
                onChange={(e) => setClover({ ...clover, merchant_id: e.target.value })}
                data-testid="payments-clover-merchant-id"
                placeholder="Clover merchant identifier"
              />
            </Field>
            <Field label={<>Device ID (serial) <span className="text-muted-foreground">(optional)</span></>}>
              <Input
                value={clover.device_id}
                onChange={(e) => setClover({ ...clover, device_id: e.target.value })}
                data-testid="payments-clover-device-id"
                placeholder="Clover Station device serial"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Required for payment-terminal pairing in a later milestone.
              </div>
            </Field>
            <Field label={<>Remote App ID (RAID) <span className="text-muted-foreground">(optional)</span></>}>
              <Input
                value={clover.remote_app_id}
                onChange={(e) => setClover({ ...clover, remote_app_id: e.target.value })}
                data-testid="payments-clover-raid"
                placeholder="From the Clover developer console"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Required for payment-terminal pairing in a later milestone.
              </div>
            </Field>
          </div>

          <div className="mt-3 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-3" data-testid="payments-clover-notice">
            <div><strong>Non-secret identifiers only on this card.</strong></div>
            <div>Save this configuration first, then enter OAuth credentials in the Clover credentials card below. Payment routing is not yet enabled — Milestone 2 covers connection foundation only.</div>
          </div>
        </Card>
      )}

      {/* Milestone 2A — Clover Connection card (replaces manual token
          entry from M2). Requires pos_config to be saved first. */}
      {mode === "external_pos"
       && posConfig?.provider === "clover"
       && posConfig?.clover?.merchant_id
       && (
        <Card className="p-5" data-testid="payments-clover-credentials-card">
          <div className="mb-3">
            <div className="font-display text-lg font-semibold">Clover connection</div>
            <div className="text-sm text-muted-foreground">
              OAuth 2.0 authorization. Tokens are generated by Clover and stored encrypted on the server — the browser never sees them.
            </div>
          </div>

          {cloverStatus?.encryption_key_configured === false && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 mb-3" data-testid="clover-key-missing-warning">
              The Clover credential encryption key is not configured in this environment. Ask your operator to set <code>CLOVER_SECRET_KEY</code>.
            </div>
          )}
          {cloverStatus?.platform_configured === false && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 mb-3" data-testid="clover-platform-missing-warning">
              The Rudras-AI Clover App is not configured on this server. Ask your operator to set <code>CLOVER_APP_ID</code>, <code>CLOVER_APP_SECRET</code>, and <code>CLOVER_OAUTH_REDIRECT_URI</code>.
            </div>
          )}

          <div className="rounded border border-slate-200 p-3 mb-3 bg-slate-50" data-testid="clover-status-panel">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Current status</div>
            {cloverStatus?.configured ? (
              <div className="text-sm">
                <div>
                  <span className="inline-flex items-center gap-1 text-emerald-800">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"/> Connected
                  </span>
                  {" "}· merchant <span className="font-medium">{cloverStatus.merchant_id}</span>
                  {" "}· env <span className="font-medium">{cloverStatus.environment}</span>
                </div>
                {cloverStatus.authorized_at && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Authorized: {cloverStatus.authorized_at}
                  </div>
                )}
                {cloverStatus.access_token_expires_at && (
                  <div className="text-xs text-muted-foreground">
                    Access token expires: {cloverStatus.access_token_expires_at}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400 inline-block"/> Disconnected</span>
                {" "}— tenants must authorize Rudras-AI to act on their behalf via Clover.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            {cloverStatus?.configured ? (
              <>
                <Button
                  variant="outline"
                  onClick={disconnectClover}
                  disabled={credsBusy}
                  data-testid="clover-disconnect-btn"
                >
                  Disconnect Clover
                </Button>
                <Button
                  onClick={connectClover}
                  disabled={credsBusy || cloverStatus?.platform_configured === false}
                  data-testid="clover-reconnect-btn"
                >
                  {credsBusy ? "…" : "Reconnect Clover"}
                </Button>
              </>
            ) : (
              <Button
                onClick={connectClover}
                disabled={credsBusy || cloverStatus?.platform_configured === false}
                data-testid="clover-connect-btn"
              >
                {credsBusy ? "…" : "Connect Clover"}
              </Button>
            )}
          </div>

          <div className="border-t border-slate-200 mt-5 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-base font-semibold">Test Connection</div>
                <div className="text-xs text-muted-foreground">
                  Calls Clover <code>GET /v3/merchants/{"{"}merchant{"}"}</code> — read-only ping. Auto-refreshes token on 401.
                </div>
              </div>
              <Button
                onClick={runTestConnection}
                disabled={testBusy || !cloverStatus?.configured}
                data-testid="clover-test-connection-btn"
              >
                {testBusy ? "Testing…" : "Test Connection"}
              </Button>
            </div>
            {lastTest && (
              <div
                className={`text-sm mt-3 rounded p-3 ${lastTest.ok ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}
                data-testid="clover-test-result"
              >
                {lastTest.ok ? (
                  <>
                    <div className="font-medium text-emerald-800">Connected</div>
                    <div className="text-xs mt-1">Merchant: <span className="font-mono">{lastTest.merchant_name || lastTest.merchant_id}</span></div>
                    {lastTest.merchant_currency && <div className="text-xs">Currency: {lastTest.merchant_currency}</div>}
                    <div className="text-xs">Environment: {lastTest.environment}</div>
                  </>
                ) : (
                  <>
                    <div className="font-medium text-rose-800">Connection failed</div>
                    <div className="text-xs mt-1">Code: <span className="font-mono">{lastTest.code}</span></div>
                    {lastTest.http_status && <div className="text-xs">Upstream HTTP: {lastTest.http_status}</div>}
                    {lastTest.message && <div className="text-xs mt-1">{lastTest.message}</div>}
                  </>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="payments-save-btn">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
